import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGenerationJobs,
  usePreviewPrompt,
  useSubmitGenerationJob,
} from '@/hooks/useGenerationJobs';
import { useTransitions } from '@/hooks/useActivities';
import GenerationConfigForm from '@/components/generation/GenerationConfigForm';
import PromptReviewStep from '@/components/generation/PromptReviewStep';
import JobStatusView from '@/components/generation/JobStatusView';
import JobListItem from '@/components/generation/JobListItem';
import type { CreateGenerationJobInput, PreviewPromptInput, GenerationJob } from '@/types/generation';

type PageView = 'list' | 'configure' | 'review-prompt' | 'submitted';

export default function ActivityGenerationPage() {
  const [view, setView] = useState<PageView>('list');
  const [configData, setConfigData] = useState<CreateGenerationJobInput | null>(null);
  const [promptText, setPromptText] = useState('');
  const [originalPromptText, setOriginalPromptText] = useState('');
  const [historicalActivityCount, setHistoricalActivityCount] = useState(0);
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);

  const { data: jobsResponse, isLoading: isLoadingJobs, refetch: refetchJobs } = useGenerationJobs();
  const { data: transitionsData, isLoading: isLoadingTransitions } = useTransitions();

  const previewPromptMutation = usePreviewPrompt();
  const submitJobMutation = useSubmitGenerationJob();

  const jobs = (jobsResponse?.data?.items ?? []) as GenerationJob[];
  const hasActiveJobs = jobs.some((j) => j.status === 'IN_PROGRESS');

  // Poll for active jobs every 30 seconds
  useEffect(() => {
    if (!hasActiveJobs) return;
    const interval = setInterval(() => refetchJobs(), 30 * 1000);
    return () => clearInterval(interval);
  }, [hasActiveJobs, refetchJobs]);

  // Update document title
  useEffect(() => {
    document.title = 'Generate Activities | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  const transitions = transitionsData?.data ?? [];

  // Step 1 → Step 2: After configuring, fetch prompt preview
  const handleConfigSubmit = async (data: CreateGenerationJobInput) => {
    setConfigData(data);
    const previewInput: PreviewPromptInput = {
      transitionId: data.transitionId,
      ageGroup: data.ageGroup,
      developmentAreas: data.developmentAreas,
      requestedCount: data.requestedCount,
      defaultDuration: data.defaultDuration,
    };
    const result = await previewPromptMutation.mutateAsync(previewInput);
    const text = result.data.interpolatedPrompt;
    setPromptText(text);
    setOriginalPromptText(text);
    setHistoricalActivityCount(result.data.historicalActivityCount);
    setView('review-prompt');
  };

  // Step 2 → Step 3: Submit the generation job
  const handleGenerate = async () => {
    if (!configData) return;
    const input: CreateGenerationJobInput = {
      ...configData,
      promptText,
    };
    const result = await submitJobMutation.mutateAsync(input);
    setSubmittedJobId(result.data.id);
    setView('submitted');
  };

  // Return to list view
  const handleBackToList = () => {
    setView('list');
    setConfigData(null);
    setPromptText('');
    setOriginalPromptText('');
    setSubmittedJobId(null);
    refetchJobs();
  };

  // View a submitted/active job
  const handleViewJob = (jobId: string) => {
    setSubmittedJobId(jobId);
    setView('submitted');
  };

  // ─── Render: Submitted / Status view ───────────────────────────────────────
  if (view === 'submitted' && submittedJobId) {
    return (
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-heading-1 text-admin-slate-400 mb-2">
            Generation Job Status
          </h1>
          <p className="text-body text-admin-slate-500">
            Track the progress of your generation job.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <JobStatusView jobId={submittedJobId} onBackToList={handleBackToList} />
        </motion.div>
      </div>
    );
  }

  // ─── Render: Review Prompt (Step 2) ────────────────────────────────────────
  if (view === 'review-prompt') {
    return (
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-heading-1 text-admin-slate-400 mb-2">
            Review Prompt
          </h1>
          <p className="text-body text-admin-slate-500">
            Review and optionally edit the prompt before generating activities.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <PromptReviewStep
            promptText={promptText}
            onPromptChange={setPromptText}
            originalPromptText={originalPromptText}
            onReset={() => setPromptText(originalPromptText)}
            onBack={() => setView('configure')}
            onGenerate={handleGenerate}
            isSubmitting={submitJobMutation.isPending}
            historicalActivityCount={historicalActivityCount}
          />
        </motion.div>
      </div>
    );
  }

  // ─── Render: Configure (Step 1) ────────────────────────────────────────────
  if (view === 'configure') {
    return (
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-heading-1 text-admin-slate-400 mb-2">
            Generate Activities
          </h1>
          <p className="text-body text-admin-slate-500">
            Configure the generation parameters.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <GenerationConfigForm
            transitions={transitions}
            isLoadingTransitions={isLoadingTransitions}
            onSubmit={handleConfigSubmit}
            onCancel={handleBackToList}
            isLoading={previewPromptMutation.isPending}
          />
        </motion.div>
      </div>
    );
  }

  // ─── Render: Job List (default) ────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-heading-1 text-admin-slate-400 mb-2">
            Activity Generation
          </h1>
          <p className="text-body text-admin-slate-500">
            Generate new activities using AI.
          </p>
        </div>
        <button
          onClick={() => setView('configure')}
          className="admin-button-primary flex items-center gap-2"
        >
          <Sparkles className="w-5 h-5" />
          <span>Generate Activities</span>
        </button>
      </motion.div>

      {/* Jobs List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="space-y-3"
      >
        {isLoadingJobs ? (
          <>
            <Skeleton className="h-28 w-full bg-admin-dark-700" />
            <Skeleton className="h-28 w-full bg-admin-dark-700" />
            <Skeleton className="h-28 w-full bg-admin-dark-700" />
          </>
        ) : jobs.length === 0 ? (
          <div className="admin-card p-12 text-center">
            <div className="w-16 h-16 bg-admin-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-admin-slate-600" />
            </div>
            <h3 className="text-heading-4 text-admin-slate-400 mb-2">
              No generation jobs yet
            </h3>
            <p className="text-body-sm text-admin-slate-500 mb-6">
              Click &quot;Generate Activities&quot; to get started.
            </p>
            <button
              onClick={() => setView('configure')}
              className="admin-button-primary"
            >
              Generate Activities
            </button>
          </div>
        ) : (
          jobs.map((job) => (
            <JobListItem
              key={job.id}
              job={job}
              onViewDetails={handleViewJob}
            />
          ))
        )}
      </motion.div>
    </div>
  );
}
